import moment from 'moment';
import { ACTIVE_LIST_KEY, mealKey, slotNoteKey } from './normalize';
import { STRUCK_SECONDS } from './selectors';
import { byPlace, slotOf } from './dayEvents';

/**
 * The rebase (docs/local-first-food-data.md, phase 3): what screens read is the server's state with
 * every op not yet answered applied over it, `entities = pending.reduce(applyOp, confirmed)`.
 *
 * applyOp() is pure and keyed by (type, v), mirroring the server's handler. An op whose type or
 * version this bundle does not know, or whose handler throws, changes nothing, so an op restored
 * from another bundle can never break startup. A target that is missing is a no-op too. Payload
 * upgraders bring an op queued by an older bundle to the version its handler takes.
 */

/** `bought:<Y-m-d>` */
export const boughtListKey = (date) => `bought:${date}`;

/** What the server calls a bought list: Grocery_List_Repository::BOUGHT_LIST_NAME_FORMAT. */
const boughtListName = (date) => moment(date, 'YYYY-MM-DD').format('MMMM D, YYYY');

/**
 * `grocery.check_off` v1, as Grocery_Check_Off_Op does it: ticked, the row moves to the day's bought
 * list, made here with no id when the store has none, and is stamped with the tap's time; unticked,
 * it moves back to the active list, keeping its `order` while it is still shown struck there and
 * going to the bottom otherwise. A bought list left empty goes. A row already where it was asked to
 * go, or moved since the tap (its list is no longer `from`), is left alone.
 */
const checkOff = (entities, { row_id: rowId, checked, date, from, at }) => {
	const row = entities.groceryRows[rowId];
	if(!row || (checked && !date)) {
		return entities;
	}
	const target = checked ? boughtListKey(date) : ACTIVE_LIST_KEY;
	if(row.list_key === target || (from && row.list_key !== from)) {
		return entities;
	}
	const groceryLists = { ...entities.groceryLists };
	let moved;
	if(checked) {
		if(!groceryLists[target]) {
			groceryLists[target] = {
				key: target,
				id: null,
				name: boughtListName(date),
				is_active: false,
				purchased_on: date,
				created_at: at,
			};
		}
		moved = { ...row, list_key: target, grocery_list_id: groceryLists[target].id, checked_off: at };
	} else {
		const struck = !!row.checked_off && Number(row.checked_off) >= Number(at) - STRUCK_SECONDS;
		let order = Number(row.order) || 0;
		if(!struck) {
			const shown = Object.values(entities.groceryRows).filter((each) => each.list_key === ACTIVE_LIST_KEY ||
				(String(each.list_key).startsWith('bought:') && !!each.checked_off && Number(each.checked_off) >= Number(at) - STRUCK_SECONDS));
			order = shown.reduce((highest, each) => Math.max(highest, Number(each.order) || 0), 0) + 1;
		}
		const active = groceryLists[ACTIVE_LIST_KEY];
		moved = { ...row, list_key: ACTIVE_LIST_KEY, grocery_list_id: active ? active.id : row.grocery_list_id, checked_off: null, order };
	}
	const groceryRows = { ...entities.groceryRows, [rowId]: moved };
	const left = row.list_key;
	if(left !== ACTIVE_LIST_KEY && groceryLists[left] && !Object.values(groceryRows).some((each) => each.list_key === left)) {
		delete groceryLists[left];
	}
	return { ...entities, groceryLists, groceryRows };
};

/**
 * `grocery.row_set` v1, as Grocery_Row_Set_Op does it: only the fields the payload names change, each
 * trimmed, and a blank one is cleared to null.
 */
const setRow = (entities, payload) => {
	const row = entities.groceryRows[payload.row_id];
	if(!row) {
		return entities;
	}
	const changed = { ...row };
	['quantity', 'notes'].filter((field) => Object.prototype.hasOwnProperty.call(payload, field)).forEach((field) => {
		changed[field] = String(payload[field] || '').trim() || null;
	});
	return { ...entities, groceryRows: { ...entities.groceryRows, [payload.row_id]: changed } };
};

/** The year, month (from 1) and day a meal or slot note carries beside its 'Y-m-d' `date`. */
const dayParts = (date) => {
	const [year, month, day] = date.split('-').map(Number);
	return { year, month, day };
};

/** The meals in slot `slot` of `date`, in their order. */
const slotMeals = (meals, date, slot) => Object.values(meals)
	.filter((meal) => meal.date === date && slotOf(meal.type) === slot)
	.sort(byPlace);

/** `meals` with slot `slot` of `date` numbered 1..n as `inOrder` has it. Only meals whose number changes are copied. */
const numbered = (meals, inOrder, always = null) => {
	const next = { ...meals };
	inOrder.forEach((meal, index) => {
		if(meal === always || Number(meal.order) !== index + 1) {
			next[mealKey(meal)] = { ...meal, order: index + 1 };
		}
	});
	return next;
};

/**
 * `meals` with `meal` placed in slot `slot` of `date` as Meal_Op::place() does it: directly after the
 * meal `placement.after_uid` names, first when that is null, and last when it is not given or names a
 * meal no longer in the slot. The slot is numbered again.
 */
const place = (meals, meal, date, slot, placement) => {
	const key = mealKey(meal);
	const others = slotMeals(meals, date, slot).filter((other) => mealKey(other) !== key);
	let index = others.length;
	if(Object.prototype.hasOwnProperty.call(placement, 'after_uid')) {
		const after = placement.after_uid;
		if(after === null || after === '') {
			index = 0;
		} else {
			const at = others.findIndex((other) => other.uid === after);
			index = at === -1 ? index : at + 1;
		}
	}
	const placed = { ...meal, ...dayParts(date), date, type: slot };
	others.splice(index, 0, placed);
	return numbered(meals, others, placed);
};

const SET_FIELDS = ['name', 'notes', 'meal_idea_id'];

/** `set`'s fields as the server keeps them: a name trimmed, and one left blank not changed. */
const settable = (set) => {
	const fields = {};
	SET_FIELDS.filter((field) => Object.prototype.hasOwnProperty.call(set || {}, field)).forEach((field) => {
		fields[field] = set[field];
	});
	if('name' in fields) {
		fields.name = String(fields.name || '').trim();
		if(!fields.name) {
			delete fields.name;
		}
	}
	if('notes' in fields) {
		fields.notes = fields.notes || '';
	}
	if('meal_idea_id' in fields && (fields.meal_idea_id === undefined || fields.meal_idea_id === '')) {
		fields.meal_idea_id = null;
	}
	return fields;
};

/**
 * `meal.create` v1, as Meal_Create_Op does it: a meal under the uid the op names, placed in its slot.
 * A uid the store has already is left as it is. It has no id until the server's answer gives it one.
 */
const createMeal = (entities, payload) => {
	const name = String(payload.name || '').trim();
	if(!payload.uid || !payload.date || !name || entities.meals[payload.uid]) {
		return entities;
	}
	const meal = {
		id: null,
		uid: payload.uid,
		name,
		notes: payload.notes || '',
		type: slotOf(payload.type),
		order: 0,
		meal_idea_id: payload.meal_idea_id === undefined || payload.meal_idea_id === '' ? null : payload.meal_idea_id,
	};
	return { ...entities, meals: place(entities.meals, meal, payload.date, slotOf(payload.type), payload) };
};

/**
 * `meal.patch` v1, as Meal_Patch_Op does it: only the fields `set` names change, and `move` places the
 * meal again, numbering the slot it left. A meal the store does not have is not made.
 */
const patchMeal = (entities, { uid, set, move }) => {
	const meal = uid ? entities.meals[uid] : null;
	if(!meal) {
		return entities;
	}
	const changed = { ...meal, ...settable(set) };
	if(!move || !move.date) {
		return { ...entities, meals: { ...entities.meals, [uid]: changed } };
	}
	const slot = slotOf(move.type);
	let meals = place(entities.meals, changed, move.date, slot, move);
	if(meal.date !== move.date || slotOf(meal.type) !== slot) {
		meals = numbered(meals, slotMeals(meals, meal.date, slotOf(meal.type)));
	}
	return { ...entities, meals };
};

/** `meal.delete` v1: the meal goes; the slot it leaves keeps its numbering, as on the server. */
const deleteMeal = (entities, { uid }) => {
	if(!uid || !entities.meals[uid]) {
		return entities;
	}
	const meals = { ...entities.meals };
	delete meals[uid];
	return { ...entities, meals };
};

/** `slot_note.set` v1: the whole note on one slot of a day; a blank one clears it. */
const setSlotNote = (entities, { date, type, notes }) => {
	if(!date) {
		return entities;
	}
	const note = { ...dayParts(date), date, type: slotOf(type), notes: String(notes || '').trim() };
	const key = slotNoteKey(note);
	const slotNotes = { ...entities.slotNotes };
	if(note.notes) {
		slotNotes[key] = note;
	} else if(slotNotes[key]) {
		delete slotNotes[key];
	} else {
		return entities;
	}
	return { ...entities, slotNotes };
};

/** Handlers by type, then version. */
const handlers = {
	'grocery.check_off': { 1: checkOff },
	'grocery.row_set': { 1: setRow },
	'meal.create': { 1: createMeal },
	'meal.patch': { 1: patchMeal },
	'meal.delete': { 1: deleteMeal },
	'slot_note.set': { 1: setSlotNote },
};

/** Payload upgraders by type, then the version they upgrade from: each returns the next version's payload. */
const upgraders = {
	'grocery.check_off': {},
	'grocery.row_set': {},
	'meal.create': {},
	'meal.patch': {},
	'meal.delete': {},
	'slot_note.set': {},
};

/** The op's payload and version after every upgrader that applies to it. */
export const upgradeOp = (op) => {
	let v = Number(op.v) || 1;
	let payload = op.payload;
	const steps = upgraders[op.type] || {};
	while(steps[v]) {
		payload = steps[v](payload);
		v += 1;
	}
	return v === op.v && payload === op.payload ? op : { ...op, v, payload };
};

/** `entities` with `op` applied, or `entities` itself for an op this bundle cannot apply. */
export const applyOp = (entities, op) => {
	try {
		const upgraded = upgradeOp(op);
		const handler = (handlers[upgraded.type] || {})[upgraded.v];
		if(!handler) {
			console.warn(`An op this version of the app does not know was left as it is: ${op.type} v${op.v}`);
			return entities;
		}
		return handler(entities, upgraded.payload || {});
	} catch(error) {
		console.error('An op could not be applied, and was left as it is.', error);
		return entities;
	}
};

/** `confirmed` with every pending op applied, in order: what screens read. */
export const rebase = (confirmed, pending) => (pending.length ? pending.reduce(applyOp, confirmed) : confirmed);

/** A few words for an op, for a message about it, such as one saying it could not be saved. */
export const describeOp = (op, entities = null) => {
	const payload = (op && op.payload) || {};
	if(op && op.type === 'grocery.check_off') {
		const row = entities && entities.groceryRows[payload.row_id];
		const name = row ? row.name : 'an item';
		return payload.checked ? `Ticking off ${name}` : `Putting ${name} back on the list`;
	}
	if(op && op.type === 'grocery.row_set') {
		const row = entities && entities.groceryRows[payload.row_id];
		return `The amount or note on ${row ? row.name : 'an item'}`;
	}
	const meal = entities && payload.uid ? entities.meals[payload.uid] : null;
	const mealName = (meal && meal.name) || payload.name || 'a meal';
	switch(op && op.type) {
		case 'meal.create':
			return `Adding ${mealName}`;
		case 'meal.patch':
			return payload.move ? `Moving ${mealName}` : `Changing ${mealName}`;
		case 'meal.delete':
			return `Removing ${mealName}`;
		case 'slot_note.set':
			return `The note on ${moment(payload.date, 'YYYY-MM-DD').format('MMMM D')}`;
		default:
			return 'A change';
	}
};
